import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiCornerUpLeft, FiCreditCard, FiDownload, FiEye, FiMinusCircle, FiRefreshCcw } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import b2bInvoiceService from '../services/b2bInvoiceService';
import { toApiDateValue, toDisplayDateValue } from '../utils/dateUtils';

const currency = (value) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(Number(value || 0));

const getOverpaidAmount = (invoice) => {
  const grand = Number(invoice?.grandTotal || 0);
  const credit = Number(invoice?.creditTotal || 0);
  const paid = Number(invoice?.paidTotal || 0);
  const refunded = Number(invoice?.refundTotal || 0);
  const effectiveGrand = Math.max(0, grand - credit);
  const netPaid = Math.max(0, paid - refunded);
  return Math.max(0, netPaid - effectiveGrand);
};

const statusBadgeClasses = {
  Draft: 'bg-gray-100 text-gray-700',
  Issued: 'bg-blue-100 text-blue-700',
  PartiallyPaid: 'bg-amber-100 text-amber-800',
  Paid: 'bg-green-100 text-green-700',
  Overdue: 'bg-red-100 text-red-700',
  Voided: 'bg-gray-200 text-gray-700',
};

const ModalShell = ({ title, children, onClose, wide = false }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
    <div className={`w-full ${wide ? 'max-w-6xl' : 'max-w-2xl'} max-h-[calc(100vh-2rem)] flex flex-col rounded-xl bg-white shadow-2xl`}>
      <div className="flex items-center justify-between border-b px-6 py-4">
        <h2 className="min-w-0 break-words text-xl font-semibold text-gray-900">{title}</h2>
        <button onClick={onClose} className="rounded-md px-2 py-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900">
          Close
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">{children}</div>
    </div>
  </div>
);

const PaymentModal = ({ open, invoice, error, onClose, onSubmit }) => {
  const [form, setForm] = useState({
    paymentDate: toApiDateValue(new Date()),
    amount: '',
    method: 'bank_transfer',
    reference: '',
    notes: '',
  });

  useEffect(() => {
    if (!open || !invoice) return;
    setForm({
      paymentDate: toApiDateValue(new Date()),
      amount: invoice.balanceDue || '',
      method: 'bank_transfer',
      reference: '',
      notes: '',
    });
  }, [open, invoice]);

  if (!open || !invoice) return null;

  return (
    <ModalShell title={`Record Payment for ${invoice.invoiceNumber}`} onClose={onClose}>
      <div className="space-y-4">
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700">Payment Date</label>
            <input
              type="date"
              value={form.paymentDate}
              onChange={(event) => setForm((current) => ({ ...current, paymentDate: event.target.value }))}
              className="mt-1 w-full rounded-md border-gray-300 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Amount</label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={form.amount}
              onChange={(event) => setForm((current) => ({ ...current, amount: event.target.value }))}
              className="mt-1 w-full rounded-md border-gray-300 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Method</label>
            <select
              value={form.method}
              onChange={(event) => setForm((current) => ({ ...current, method: event.target.value }))}
              className="mt-1 w-full rounded-md border-gray-300 shadow-sm"
            >
              <option value="cash">Cash</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="card">Card</option>
              <option value="stripe">Stripe</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Reference</label>
            <input
              type="text"
              value={form.reference}
              onChange={(event) => setForm((current) => ({ ...current, reference: event.target.value }))}
              className="mt-1 w-full rounded-md border-gray-300 shadow-sm"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Notes</label>
          <textarea
            rows="3"
            value={form.notes}
            onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
            className="mt-1 w-full rounded-md border-gray-300 shadow-sm"
          />
        </div>
        <div className="flex justify-end gap-3 border-t pt-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() =>
              onSubmit({
                paymentDate: form.paymentDate,
                amount: Number(form.amount || 0),
                method: form.method,
                reference: form.reference,
                notes: form.notes,
              })
            }
            className="rounded-md bg-primary px-4 py-2 font-medium text-white hover:bg-primary-dark"
          >
            Record Payment
          </button>
        </div>
      </div>
    </ModalShell>
  );
};

const CreditNoteModal = ({ open, invoice, error, onClose, onSubmit }) => {
  const [form, setForm] = useState({
    creditDate: toApiDateValue(new Date()),
    amount: '',
    reason: 'Returned goods',
    notes: '',
  });

  useEffect(() => {
    if (!open || !invoice) return;
    const remaining = Math.max(0, Number(invoice.grandTotal || 0) - Number(invoice.creditTotal || 0));
    setForm({
      creditDate: toApiDateValue(new Date()),
      amount: remaining || '',
      reason: 'Returned goods',
      notes: '',
    });
  }, [open, invoice]);

  if (!open || !invoice) return null;

  return (
    <ModalShell title={`Create Credit Note for ${invoice.invoiceNumber}`} onClose={onClose}>
      <div className="space-y-4">
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700">Credit Date</label>
            <input
              type="date"
              value={form.creditDate}
              onChange={(event) => setForm((current) => ({ ...current, creditDate: event.target.value }))}
              className="mt-1 w-full rounded-md border-gray-300 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Amount</label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={form.amount}
              onChange={(event) => setForm((current) => ({ ...current, amount: event.target.value }))}
              className="mt-1 w-full rounded-md border-gray-300 shadow-sm"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Reason</label>
          <input
            type="text"
            value={form.reason}
            onChange={(event) => setForm((current) => ({ ...current, reason: event.target.value }))}
            className="mt-1 w-full rounded-md border-gray-300 shadow-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Notes</label>
          <textarea
            rows="3"
            value={form.notes}
            onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
            className="mt-1 w-full rounded-md border-gray-300 shadow-sm"
          />
        </div>
        <div className="flex justify-end gap-3 border-t pt-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() =>
              onSubmit({
                creditDate: form.creditDate,
                amount: Number(form.amount || 0),
                reason: form.reason,
                notes: form.notes,
              })
            }
            className="rounded-md bg-primary px-4 py-2 font-medium text-white hover:bg-primary-dark"
          >
            Create Credit Note
          </button>
        </div>
      </div>
    </ModalShell>
  );
};

const RefundModal = ({ open, invoice, error, onClose, onSubmit }) => {
  const [form, setForm] = useState({
    refundDate: toApiDateValue(new Date()),
    amount: '',
    method: 'bank_transfer',
    reference: '',
    notes: '',
  });

  useEffect(() => {
    if (!open || !invoice) return;
    const overpaidAmount = getOverpaidAmount(invoice);
    setForm({
      refundDate: toApiDateValue(new Date()),
      amount: overpaidAmount || '',
      method: 'bank_transfer',
      reference: '',
      notes: '',
    });
  }, [open, invoice]);

  if (!open || !invoice) return null;

  return (
    <ModalShell title={`Record Refund for ${invoice.invoiceNumber}`} onClose={onClose}>
      <div className="space-y-4">
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Available overpaid amount: {currency(getOverpaidAmount(invoice))}
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700">Refund Date</label>
            <input
              type="date"
              value={form.refundDate}
              onChange={(event) => setForm((current) => ({ ...current, refundDate: event.target.value }))}
              className="mt-1 w-full rounded-md border-gray-300 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Amount</label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={form.amount}
              onChange={(event) => setForm((current) => ({ ...current, amount: event.target.value }))}
              className="mt-1 w-full rounded-md border-gray-300 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Method</label>
            <select
              value={form.method}
              onChange={(event) => setForm((current) => ({ ...current, method: event.target.value }))}
              className="mt-1 w-full rounded-md border-gray-300 shadow-sm"
            >
              <option value="cash">Cash</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="card">Card</option>
              <option value="stripe">Stripe</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Reference</label>
            <input
              type="text"
              value={form.reference}
              onChange={(event) => setForm((current) => ({ ...current, reference: event.target.value }))}
              className="mt-1 w-full rounded-md border-gray-300 shadow-sm"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Notes</label>
          <textarea
            rows="3"
            value={form.notes}
            onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
            className="mt-1 w-full rounded-md border-gray-300 shadow-sm"
          />
        </div>
        <div className="flex justify-end gap-3 border-t pt-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() =>
              onSubmit({
                refundDate: form.refundDate,
                amount: Number(form.amount || 0),
                method: form.method,
                reference: form.reference,
                notes: form.notes,
              })
            }
            className="rounded-md bg-primary px-4 py-2 font-medium text-white hover:bg-primary-dark"
          >
            Record Refund
          </button>
        </div>
      </div>
    </ModalShell>
  );
};

const InvoiceDetailModal = ({ open, invoice, onClose }) => {
  const [linePage, setLinePage] = useState(1);
  const [linePageSize, setLinePageSize] = useState(10);

  useEffect(() => {
    if (open) {
      setLinePage(1);
    }
  }, [open, invoice?._id]);

  const lines = invoice?.lines || [];
  const totalLinePages = Math.max(1, Math.ceil(lines.length / linePageSize));
  const currentLinePage = Math.min(linePage, totalLinePages);
  const lineStartIndex = (currentLinePage - 1) * linePageSize;
  const pagedLines = lines.slice(lineStartIndex, lineStartIndex + linePageSize);
  const lineRangeStart = lines.length ? lineStartIndex + 1 : 0;
  const lineRangeEnd = lines.length ? lineStartIndex + pagedLines.length : 0;

  useEffect(() => {
    if (linePage !== currentLinePage) {
      setLinePage(currentLinePage);
    }
  }, [currentLinePage, linePage]);

  if (!open || !invoice) return null;
  const overpaidAmount = getOverpaidAmount(invoice);
  const effectiveGrandTotal = Math.max(0, Number(invoice.grandTotal || 0) - Number(invoice.creditTotal || 0));
  const netPaidTotal = Math.max(0, Number(invoice.paidTotal || 0) - Number(invoice.refundTotal || 0));

  return (
    <ModalShell title={`B2B Invoice ${invoice.invoiceNumber}`} onClose={onClose} wide>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-3 rounded-lg border border-gray-200 p-4">
          <h3 className="text-lg font-semibold text-gray-900">Overview</h3>
          <p><span className="font-medium">Customer:</span> {invoice.customerSnapshot?.businessName || invoice.customerSnapshot?.fullName || 'Customer'}</p>
          <p><span className="font-medium">Order:</span> {invoice.order?.orderNumber || 'Linked order'}</p>
          <p><span className="font-medium">Status:</span> {invoice.status}</p>
          <p><span className="font-medium">Customer Ref:</span> {invoice.customerReference || 'None'}</p>
          <p><span className="font-medium">Invoice Date:</span> {toDisplayDateValue(invoice.invoiceDate) || 'Not set'}</p>
          <p><span className="font-medium">Due Date:</span> {toDisplayDateValue(invoice.dueDate) || 'Not set'}</p>
          <p><span className="font-medium">Expected:</span> {toDisplayDateValue(invoice.expectedFulfillmentDate) || 'Not set'}</p>
          <p><span className="font-medium">Grand Total:</span> {currency(invoice.grandTotal)}</p>
          <p><span className="font-medium">Credits:</span> {currency(invoice.creditTotal)}</p>
          <p><span className="font-medium">Effective Total:</span> {currency(effectiveGrandTotal)}</p>
          <p><span className="font-medium">Payments Received:</span> {currency(invoice.paidTotal)}</p>
          <p><span className="font-medium">Refunds Paid Out:</span> {currency(invoice.refundTotal)}</p>
          <p><span className="font-medium">Net Paid:</span> {currency(netPaidTotal)}</p>
          <p><span className="font-medium">Balance:</span> {currency(invoice.balanceDue)}</p>
          <p><span className="font-medium">Overpaid:</span> {currency(overpaidAmount)}</p>
        </div>
        <div className="space-y-4 lg:col-span-2">
          <div className="rounded-lg border border-gray-200 p-4">
            <h3 className="mb-3 text-lg font-semibold text-gray-900">Invoice Lines</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">Item</th>
                    <th className="px-4 py-2 text-right">Qty</th>
                    <th className="px-4 py-2 text-right">Unit Price</th>
                    <th className="px-4 py-2 text-right">Discount</th>
                    <th className="px-4 py-2 text-right">Tax</th>
                    <th className="px-4 py-2 text-right">Net</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {pagedLines.map((line, index) => (
                    <tr key={`${line.product || line.barcode || line.name}-${index}`}>
                      <td className="px-4 py-2">
                        <div className="font-medium text-gray-900">{line.name}</div>
                        <div className="text-xs text-gray-500">{line.sku || line.barcode || 'No SKU/barcode'}</div>
                      </td>
                      <td className="px-4 py-2 text-right">{line.quantity}</td>
                      <td className="px-4 py-2 text-right">{currency(line.unitPrice)}</td>
                      <td className="px-4 py-2 text-right">
                        {currency(Number(line.lineDiscountAmount || 0) + Number(line.orderDiscountShare || 0))}
                      </td>
                      <td className="px-4 py-2 text-right">{currency(line.taxAmount)}</td>
                      <td className="px-4 py-2 text-right">{currency(line.lineNetTotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 flex flex-col gap-2 border-t border-gray-200 pt-3 text-sm text-gray-600 sm:flex-row sm:items-center sm:justify-between">
              <span>
                Showing {lineRangeStart}-{lineRangeEnd} of {lines.length} line items
              </span>
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium uppercase tracking-wide text-gray-500">Rows</label>
                <select
                  value={linePageSize}
                  onChange={(event) => {
                    setLinePageSize(Number(event.target.value || 10));
                    setLinePage(1);
                  }}
                  className="rounded-md border-gray-300 py-1 text-sm"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <button
                  type="button"
                  onClick={() => setLinePage((current) => Math.max(1, current - 1))}
                  disabled={currentLinePage <= 1}
                  className="rounded-md border border-gray-300 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Prev
                </button>
                <span>
                  Page {currentLinePage} / {totalLinePages}
                </span>
                <button
                  type="button"
                  onClick={() => setLinePage((current) => Math.min(totalLinePages, current + 1))}
                  disabled={currentLinePage >= totalLinePages}
                  className="rounded-md border border-gray-300 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 p-4">
            <h3 className="mb-3 text-lg font-semibold text-gray-900">Payments</h3>
            {invoice.payments?.length ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left">Date</th>
                      <th className="px-4 py-2 text-left">Type</th>
                      <th className="px-4 py-2 text-left">Method</th>
                      <th className="px-4 py-2 text-left">Reference</th>
                      <th className="px-4 py-2 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {invoice.payments.map((payment) => (
                      <tr key={payment._id}>
                        <td className="px-4 py-2">{toDisplayDateValue(payment.paymentDate) || 'Not set'}</td>
                        <td className="px-4 py-2">{payment.type === 'refund' ? 'Refund' : 'Payment'}</td>
                        <td className="px-4 py-2">{payment.method}</td>
                        <td className="px-4 py-2">{payment.reference || 'None'}</td>
                        <td className="px-4 py-2 text-right">
                          {payment.type === 'refund' ? `-${currency(payment.amount)}` : currency(payment.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No payments recorded yet.</p>
            )}
          </div>
        </div>
      </div>
    </ModalShell>
  );
};

const B2BInvoicesPage = () => {
  const { activeStore } = useStore();
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [pageSize, setPageSize] = useState(25);
  const [filters, setFilters] = useState({ status: '', invoiceNumber: '', overdueOnly: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentModalError, setPaymentModalError] = useState('');
  const [creditModalError, setCreditModalError] = useState('');
  const [refundModalError, setRefundModalError] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [creditModalOpen, setCreditModalOpen] = useState(false);
  const [refundModalOpen, setRefundModalOpen] = useState(false);

  const canRead = user?.permissions?.includes('b2b_invoices:read');
  const canRecordPayment = user?.permissions?.includes('b2b_invoices:record_payment');
  const activeStoreId = activeStore?._id;

  const loadInvoices = useCallback(async (page = 1, limit = pageSize) => {
    if (!activeStoreId || !canRead) return;
    setLoading(true);
    setError('');
    try {
      const response = await b2bInvoiceService.fetchInvoices({
        storeId: activeStoreId,
        status: filters.status || undefined,
        invoiceNumber: filters.invoiceNumber || undefined,
        overdueOnly: filters.overdueOnly || undefined,
        page,
        limit,
      });
      setInvoices(response.data || []);
      setPagination(response.pagination || { page, pages: 1, total: response.data?.length || 0, limit });
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load B2B invoices.');
    } finally {
      setLoading(false);
    }
  }, [activeStoreId, canRead, filters.invoiceNumber, filters.overdueOnly, filters.status, pageSize]);

  useEffect(() => {
    if (!activeStoreId || !canRead) return;
    loadInvoices(1);
  }, [activeStoreId, canRead, loadInvoices]);

  const openInvoiceDetail = async (invoice) => {
    try {
      const detailed = await b2bInvoiceService.getInvoice(invoice._id);
      setSelectedInvoice(detailed);
      setDetailModalOpen(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load invoice details.');
    }
  };

  const openPaymentModal = async (invoice) => {
    try {
      const detailed = await b2bInvoiceService.getInvoice(invoice._id);
      setSelectedInvoice(detailed);
      setPaymentModalError('');
      setPaymentModalOpen(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load invoice for payment.');
    }
  };

  const openCreditModal = async (invoice) => {
    try {
      const detailed = await b2bInvoiceService.getInvoice(invoice._id);
      setSelectedInvoice(detailed);
      setCreditModalError('');
      setCreditModalOpen(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load invoice for credit note.');
    }
  };

  const openRefundModal = async (invoice) => {
    try {
      const detailed = await b2bInvoiceService.getInvoice(invoice._id);
      setSelectedInvoice(detailed);
      setRefundModalError('');
      setRefundModalOpen(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load invoice for refund.');
    }
  };

  const handleRecordPayment = async (payload) => {
    try {
      await b2bInvoiceService.recordPayment(selectedInvoice._id, payload);
      setPaymentModalOpen(false);
      setSelectedInvoice(null);
      setPaymentModalError('');
      loadInvoices(pagination.page);
    } catch (err) {
      setPaymentModalError(err.response?.data?.message || 'Unable to record payment.');
    }
  };

  const handleCreateCreditNote = async (payload) => {
    try {
      await b2bInvoiceService.createCreditNote(selectedInvoice._id, payload);
      setCreditModalOpen(false);
      setSelectedInvoice(null);
      setCreditModalError('');
      loadInvoices(pagination.page);
    } catch (err) {
      setCreditModalError(err.response?.data?.message || 'Unable to create credit note.');
    }
  };

  const handleRecordRefund = async (payload) => {
    try {
      await b2bInvoiceService.recordRefund(selectedInvoice._id, payload);
      setRefundModalOpen(false);
      setSelectedInvoice(null);
      setRefundModalError('');
      loadInvoices(pagination.page);
    } catch (err) {
      setRefundModalError(err.response?.data?.message || 'Unable to record refund.');
    }
  };

  const handleDownloadPdf = async (invoice) => {
    try {
      const blob = await b2bInvoiceService.generatePdf(invoice._id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${invoice.invoiceNumber || 'b2b-invoice'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to generate invoice PDF.');
    }
  };

  const invoiceRows = useMemo(() => invoices || [], [invoices]);
  const invoiceRangeStart = invoiceRows.length
    ? (Math.max(1, Number(pagination.page || 1)) - 1) * Number(pagination.limit || pageSize) + 1
    : 0;
  const invoiceRangeEnd = invoiceRows.length ? invoiceRangeStart + invoiceRows.length - 1 : 0;

  if (!canRead) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-red-600">Access Denied</h1>
        <p className="mt-2">You do not have permission to view B2B invoices.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">B2B Invoices</h1>
          <p className="text-gray-500">Track AR invoices, balances due, customer payments, and downloadable invoice PDFs.</p>
        </div>
        <button
          onClick={() => loadInvoices(pagination.page)}
          className="inline-flex items-center rounded-md border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
        >
          <FiRefreshCcw className="mr-2" />
          Refresh
        </button>
      </div>

      {error && <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-700">{error}</div>}

      <div className="mb-6 grid grid-cols-1 gap-4 rounded-xl bg-white p-4 shadow md:grid-cols-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Status</label>
          <select
            value={filters.status}
            onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}
            className="mt-1 w-full rounded-md border-gray-300 shadow-sm"
          >
            <option value="">All statuses</option>
            {Object.keys(statusBadgeClasses).map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700">Invoice Number</label>
          <input
            type="text"
            value={filters.invoiceNumber}
            onChange={(event) => setFilters((current) => ({ ...current, invoiceNumber: event.target.value }))}
            placeholder="Search by invoice number"
            className="mt-1 w-full rounded-md border-gray-300 shadow-sm"
          />
        </div>
        <label className="flex items-end gap-3 rounded-md border border-gray-200 px-4 py-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={filters.overdueOnly}
            onChange={(event) => setFilters((current) => ({ ...current, overdueOnly: event.target.checked }))}
            className="rounded border-gray-300 text-primary focus:ring-primary"
          />
          Overdue only
        </label>
      </div>

      <div className="overflow-hidden rounded-xl bg-white shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Invoice</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Customer</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Due Date</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Grand Total</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Balance</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {!loading && invoiceRows.length === 0 && (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                    No B2B invoices found for the selected filters.
                  </td>
                </tr>
              )}
              {invoiceRows.map((invoice) => (
                <tr key={invoice._id}>
                  <td className="px-4 py-4">
                    <div className="font-medium text-gray-900">{invoice.invoiceNumber}</div>
                    <div className="text-xs text-gray-500">{toDisplayDateValue(invoice.invoiceDate) || 'Not set'}</div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="font-medium text-gray-900">
                      {invoice.customer?.b2bProfile?.businessName ||
                        `${invoice.customer?.firstName || ''} ${invoice.customer?.lastName || ''}`.trim() ||
                        invoice.customerSnapshot?.businessName ||
                        invoice.customerSnapshot?.fullName}
                    </div>
                    <div className="text-xs text-gray-500">{invoice.order?.orderNumber || 'Linked order'}</div>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${statusBadgeClasses[invoice.status] || 'bg-gray-100 text-gray-700'}`}>
                      {invoice.status}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-700">{toDisplayDateValue(invoice.dueDate) || 'Not set'}</td>
                  <td className="px-4 py-4 text-right font-medium text-gray-900">{currency(invoice.grandTotal)}</td>
                  <td className="px-4 py-4 text-right font-semibold text-gray-900">{currency(invoice.balanceDue)}</td>
                  <td className="px-4 py-4">
                    <div className="flex flex-wrap justify-end gap-2">
                      <button
                        onClick={() => openInvoiceDetail(invoice)}
                        className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        title="View"
                      >
                        <FiEye />
                      </button>
                      {canRecordPayment && ['Issued', 'PartiallyPaid', 'Overdue'].includes(invoice.status) && (
                        <button
                          onClick={() => openPaymentModal(invoice)}
                          className="rounded-md border border-emerald-200 px-3 py-2 text-sm text-emerald-700 hover:bg-emerald-50"
                          title="Record payment"
                        >
                          <FiCreditCard />
                        </button>
                      )}
                      {canRecordPayment && invoice.status !== 'Voided' && (
                        <button
                          onClick={() => openCreditModal(invoice)}
                          className="rounded-md border border-amber-200 px-3 py-2 text-sm text-amber-700 hover:bg-amber-50"
                          title="Create credit note"
                        >
                          <FiMinusCircle />
                        </button>
                      )}
                      {canRecordPayment && invoice.status !== 'Voided' && getOverpaidAmount(invoice) > 0.0001 && (
                        <button
                          onClick={() => openRefundModal(invoice)}
                          className="rounded-md border border-purple-200 px-3 py-2 text-sm text-purple-700 hover:bg-purple-50"
                          title="Record refund payout"
                        >
                          <FiCornerUpLeft />
                        </button>
                      )}
                      <button
                        onClick={() => handleDownloadPdf(invoice)}
                        className="rounded-md border border-blue-200 px-3 py-2 text-sm text-blue-700 hover:bg-blue-50"
                        title="Download PDF"
                      >
                        <FiDownload />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="mt-3 flex flex-col gap-3 rounded-xl bg-white px-4 py-3 shadow sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm text-gray-600">
          Showing {invoiceRangeStart}-{invoiceRangeEnd} of {pagination.total || 0} invoices
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-xs font-medium uppercase tracking-wide text-gray-500">Rows</label>
          <select
            value={pageSize}
            onChange={(event) => {
              const nextLimit = Number(event.target.value || 25);
              setPageSize(nextLimit);
              loadInvoices(1, nextLimit);
            }}
            className="rounded-md border-gray-300 py-1 text-sm"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <button
            type="button"
            onClick={() => loadInvoices(Number(pagination.page || 1) - 1)}
            disabled={loading || Number(pagination.page || 1) <= 1}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-50"
          >
            Prev
          </button>
          <span className="text-sm text-gray-600">
            Page {Math.max(1, Number(pagination.page || 1))} / {Math.max(1, Number(pagination.pages || 1))}
          </span>
          <button
            type="button"
            onClick={() => loadInvoices(Number(pagination.page || 1) + 1)}
            disabled={loading || Number(pagination.page || 1) >= Number(pagination.pages || 1)}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      <InvoiceDetailModal
        open={detailModalOpen}
        invoice={selectedInvoice}
        onClose={() => {
          setDetailModalOpen(false);
          setSelectedInvoice(null);
        }}
      />

      <PaymentModal
        open={paymentModalOpen}
        invoice={selectedInvoice}
        error={paymentModalError}
        onClose={() => {
          setPaymentModalOpen(false);
          setSelectedInvoice(null);
          setPaymentModalError('');
        }}
        onSubmit={handleRecordPayment}
      />

      <CreditNoteModal
        open={creditModalOpen}
        invoice={selectedInvoice}
        error={creditModalError}
        onClose={() => {
          setCreditModalOpen(false);
          setSelectedInvoice(null);
          setCreditModalError('');
        }}
        onSubmit={handleCreateCreditNote}
      />

      <RefundModal
        open={refundModalOpen}
        invoice={selectedInvoice}
        error={refundModalError}
        onClose={() => {
          setRefundModalOpen(false);
          setSelectedInvoice(null);
          setRefundModalError('');
        }}
        onSubmit={handleRecordRefund}
      />
    </div>
  );
};

export default B2BInvoicesPage;
